/**
 * Pure typewriter timing (no Phaser). Used by the dialogue box and the letter panel; unit tested.
 * All timings are deterministic functions of the text and the text-speed setting.
 */
import type { Settings } from '@/core/types';

export type TextSpeed = Settings['textSpeed'];

/** Base milliseconds per CJK character. */
export const CHAR_MS: Record<TextSpeed, number> = { slow: 64, normal: 36, fast: 16, instant: 0 };

/** Short breath (读逗). */
const SOFT_PAUSE = '，、,';
/** Full stop (句末停顿). */
const HARD_PAUSE = '。！？；：…—.!?;:';
/** Closing marks that should not carry their own pause but delay the preceding one. */
const CLOSERS = '”』」》）〕】\'")]';

export interface TypewriterOptions {
  /** Multiplies the base per-character delay (letters use ~1.6 for a brush feel). */
  multiplier?: number;
  /** Multiplies the punctuation pauses. */
  pauseScale?: number;
}

function isAscii(ch: string): boolean {
  const code = ch.codePointAt(0) ?? 0;
  return code < 0x2e80;
}

/** Delay (ms) before the character at index `i` becomes visible, given the previous character. */
export function charDelay(chars: readonly string[], i: number, speed: TextSpeed, opts: TypewriterOptions = {}): number {
  const base = CHAR_MS[speed] * (opts.multiplier ?? 1);
  if (base <= 0) return 0;
  const pauseScale = opts.pauseScale ?? 1;
  const ch = chars[i] ?? '';
  const prev = i > 0 ? (chars[i - 1] ?? '') : '';
  let ms: number;
  if (ch === '\n') ms = base * 0.5;
  else if (ch === ' ' || ch === '　') ms = base * 0.5;
  else if (CLOSERS.includes(ch)) ms = base * 0.3;
  else if (isAscii(ch)) ms = base * 0.6;
  else ms = base;
  // Pause AFTER punctuation: charged on the next visible character. Closing quotes defer the pause.
  if (prev && !CLOSERS.includes(ch)) {
    if (SOFT_PAUSE.includes(prev)) ms += base * 3 * pauseScale;
    else if (HARD_PAUSE.includes(prev)) ms += base * 7 * pauseScale;
    else if (CLOSERS.includes(prev)) {
      const beforeCloser = i > 1 ? (chars[i - 2] ?? '') : '';
      if (SOFT_PAUSE.includes(beforeCloser)) ms += base * 3 * pauseScale;
      else if (HARD_PAUSE.includes(beforeCloser)) ms += base * 7 * pauseScale;
    } else if (prev === '\n') ms += base * 4 * pauseScale;
  }
  return Math.round(ms);
}

/** Splits into user-perceived characters (code points), so surrogate pairs count once. */
export function splitChars(text: string): string[] {
  return Array.from(text);
}

/**
 * Cumulative reveal schedule: `schedule[k]` is the time (ms from start) at which `k + 1` characters
 * are visible. Instant speed yields all zeros.
 */
export function buildSchedule(text: string, speed: TextSpeed, opts: TypewriterOptions = {}): number[] {
  const chars = splitChars(text);
  const out: number[] = new Array(chars.length);
  let t = 0;
  for (let i = 0; i < chars.length; i++) {
    t += charDelay(chars, i, speed, opts);
    out[i] = t;
  }
  return out;
}

/** Total duration of a text at a speed (ms). */
export function totalDuration(text: string, speed: TextSpeed, opts: TypewriterOptions = {}): number {
  const s = buildSchedule(text, speed, opts);
  return s.length ? (s[s.length - 1] ?? 0) : 0;
}

/**
 * Stateful reveal cursor. Feed `tick(dtMs)`; read `visible` (character count) and `done`.
 * `complete()` reveals everything immediately.
 */
export class Typewriter {
  readonly chars: string[];
  private readonly schedule: number[];
  private elapsed = 0;
  visible = 0;

  constructor(text: string, speed: TextSpeed, opts: TypewriterOptions = {}) {
    this.chars = splitChars(text);
    this.schedule = buildSchedule(text, speed, opts);
    if (speed === 'instant') this.visible = this.chars.length;
    else this.advanceTo(0);
  }

  get length(): number { return this.chars.length; }
  get done(): boolean { return this.visible >= this.chars.length; }
  get text(): string { return this.chars.slice(0, this.visible).join(''); }

  /** Advances time; returns true when the visible count changed. */
  tick(dtMs: number): boolean {
    if (this.done) return false;
    this.elapsed += Math.max(0, dtMs);
    const before = this.visible;
    this.advanceTo(this.elapsed);
    return this.visible !== before;
  }

  complete(): void {
    this.visible = this.chars.length;
    this.elapsed = this.schedule.length ? (this.schedule[this.schedule.length - 1] ?? 0) : 0;
  }

  private advanceTo(t: number): void {
    while (this.visible < this.chars.length && (this.schedule[this.visible] ?? 0) <= t) this.visible++;
  }
}
